import React, { ReactNode, useState } from 'react';

import isEqual from 'lodash/isEqual';

import { useDialog } from '@vk-hr-tek/ui/hooks';

import {
  ContentSurveysFormValues,
  DateExplanationBadgeModule,
  ExternalDeps,
} from '../../types';
import {
  ContentSurveyCard,
  ContentSurveysDialog,
  DeleteContentSurveysDialog,
} from '../../segments';

import { ContentSurveys } from './ContentSurveys';

type DialogTypes = 'form' | 'delete';

interface ContentSurveysPluginProps {
  label: ReactNode;
  bgWhite?: boolean;
  externalDeps: ExternalDeps;
  onSubscribe?: (list: ContentSurveysFormValues[]) => void;
  module?: DateExplanationBadgeModule;
}

function hasChanges(
  externalItem: ContentSurveysFormValues | undefined,
  formData: ContentSurveysFormValues,
): boolean {
  if (!externalItem) return false;

  const normalizedExternal = {
    name: externalItem.common.name,
    questions: (externalItem.questions ?? []).map(
      ({ answerSettings, content }) => ({
        answerSettings: answerSettings.map(({ id, ...rest }) => rest),
        content: {
          ...content,
          data:
            typeof content.data === 'string'
              ? JSON.parse(content.data)
              : content.data,
        },
      }),
    ),
    anonymous: externalItem.settings.anonymous,
  };

  const normalizedForm = {
    name: formData.common.name,
    questions: formData.questions.map(({ answerSettings, content }) => ({
      answerSettings: answerSettings.map(({ id, ...rest }) => rest),
      content: {
        ...content,
        data:
          typeof content.data === 'string'
            ? JSON.parse(content.data)
            : content.data,
      },
    })),
    anonymous: formData.settings.anonymous,
  };

  return !isEqual(normalizedExternal, normalizedForm);
}

export const ContentSurveysPlugin = ({
  label,
  onSubscribe,
  bgWhite = false,
  externalDeps,
  module,
}: ContentSurveysPluginProps) => {
  const { onClose, onOpen, dialogsState } = useDialog<DialogTypes>({
    form: false,
    delete: false,
  });

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const [listOfContentSurvey, setListOfContentSurvey] = useState<
    ContentSurveysFormValues[]
  >(externalDeps?.externalList ?? []);

  const handleUpdateListOfContentSurvey = (
    formData: ContentSurveysFormValues,
  ) => {
    setListOfContentSurvey((prev) => {
      if (selectedIndex !== null) {
        const newList = [...prev];

        const newQuestions = formData.questions.map(
          ({ answerSettings, content, id }) => ({
            id,
            answerSettings,
            content,
          }),
        );

        newList[selectedIndex] = {
          ...formData,
          questions: newQuestions,
          meta: {
            ...(formData?.meta ?? {}),
            modified: hasChanges(
              externalDeps.externalList?.[selectedIndex],
              formData,
            ),
          },
        };

        onSubscribe?.(newList);

        return newList;
      } else {
        const newList = [...prev, formData];
        onSubscribe?.(newList);
        return newList;
      }
    });

    setSelectedIndex(null);
  };

  const handleOpenUpdateDialog = (index: number) => () => {
    setSelectedIndex(index);
    onOpen('form')();
  };

  const handleOpenDeleteDialog = (index: number) => () => {
    setSelectedIndex(index);
    onOpen('delete')();
  };

  const handleDelete = () => {
    if (selectedIndex === null) return;

    setListOfContentSurvey((prev) => {
      const newList = prev.filter((_, index) => index !== selectedIndex);
      onSubscribe?.(newList);
      return newList;
    });

    onClose('delete')();
  };

  const handleCloseContentSurveysDialog = () => {
    setSelectedIndex(null);
    onClose('form')();
  };

  const selectedContentSurvey =
    listOfContentSurvey.find((_, index) => index === selectedIndex) ?? null;

  return (
    <>
      <ContentSurveys label={label} bgWhite={bgWhite} onCreate={onOpen('form')}>
        {!!listOfContentSurvey.length &&
          listOfContentSurvey.map((contentSurvey, index) => (
            <ContentSurveyCard
              key={index}
              bgWhite={bgWhite}
              externalDeps={externalDeps}
              contentSurvey={contentSurvey}
              onUpdate={handleOpenUpdateDialog(index)}
              onDelete={handleOpenDeleteDialog(index)}
              module={module}
            />
          ))}
      </ContentSurveys>

      <ContentSurveysDialog
        isOpen={dialogsState.form}
        onSave={handleUpdateListOfContentSurvey}
        onClose={handleCloseContentSurveysDialog}
        editContentSurvey={selectedContentSurvey}
      />

      <DeleteContentSurveysDialog
        onDelete={handleDelete}
        isOpen={dialogsState.delete}
        onClose={onClose('delete')}
        contentSurvey={selectedContentSurvey}
      />
    </>
  );
};
